// src/store/slices/locationSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { location } from "../../types";

// Initial state for the location slice
const initialState: location.LocationStateType = {
  states: {
    isLoading: false,
    data: [],
    error: null,
  },
  cities: {
    isLoading: false,
    data: [],
    error: null,
  },
  getAddressList: {
    isLoading: false,
    data: null,
    error: null,
  },
  AddUpdateAddres: {
    data: null,
    isLoading: false,
    error: null,
  },
  DeleteAddres: {
    data: null,
    isLoading: false,
    error: null,
  },
  pincodeValidation: {
    data: null,
    isLoading: false,
    error: null,
  },
};

// Define the location slice
const locationSlice = createSlice({
  name: "location",
  initialState,
  reducers: {
    // get states
    getStatesListRequest: (
      state: location.LocationStateType,
      action: PayloadAction<any>
    ) => {
      state.states.isLoading = true;
      state.states.data = [];
      state.states.error = null;
    },
    getStatesListSuccess: (
      state: location.LocationStateType,
      action: PayloadAction<any>
    ) => {
      state.states.isLoading = false;
      state.states.data = action.payload;
      state.states.error = null;
    },
    getStatesListError: (
      state: location.LocationStateType,
      { payload: error }: PayloadAction<string>
    ) => {
      state.states.isLoading = false;
      state.states.data = [];
      state.states.error = error;
    },

    // get cities
    getCitiesListRequest: (
      state: location.LocationStateType,
      action: PayloadAction<any>
    ) => {
      state.cities.isLoading = true;
      state.cities.data = [];
      state.cities.error = null;
    },
    getCitiesListSuccess: (
      state: location.LocationStateType,
      action: PayloadAction<any>
    ) => {
      state.cities.isLoading = false;
      state.cities.data = action.payload;
      state.cities.error = null;
    },
    getCitiesListError: (
      state: location.LocationStateType,
      { payload: error }: PayloadAction<string>
    ) => {
      state.cities.isLoading = false;
      state.cities.data = [];
      state.cities.error = error;
    },
    // address list
    getAddressListRequest: (
      state: location.LocationStateType,
      action: PayloadAction<any>
    ) => {
      state.getAddressList.isLoading = true;
      state.getAddressList.error = null;
    },
    getAddressListSuccess: (
      state: location.LocationStateType,
      action: PayloadAction<any>
    ) => {
      state.getAddressList.isLoading = false;
      state.getAddressList.data = action.payload;
    },
    getAddressListError: (
      state: location.LocationStateType,
      { payload: error }: PayloadAction<string>
    ) => {
      state.getAddressList.isLoading = false;
      state.getAddressList.error = error;
    },
    //add and update address
    updateAddressRequest: (
      state: location.LocationStateType,
      action: PayloadAction<any>
    ) => {
      state.AddUpdateAddres.isLoading = true;
      state.AddUpdateAddres.error = null;
    },
    updateAddressSuccess: (
      state: location.LocationStateType,
      action: PayloadAction<any>
    ) => {
      state.AddUpdateAddres.isLoading = false;
      state.AddUpdateAddres.data = action.payload?.data;
    },
    updateAddressError: (
      state: location.LocationStateType,
      { payload: error }: PayloadAction<string>
    ) => {
      state.AddUpdateAddres.isLoading = false;
      state.AddUpdateAddres.error = error;
    },
    //delete address
    deleteAddressRequest: (
      state: location.LocationStateType,
      action: PayloadAction<any>
    ) => {
      state.DeleteAddres.isLoading = true;
      state.DeleteAddres.error = null;
    },
    deleteAddressSuccess: (
      state: location.LocationStateType,
      action: PayloadAction<any>
    ) => {
      state.DeleteAddres.isLoading = false;
      state.DeleteAddres.data = action.payload;

      const data =
        state.getAddressList.data?.filter((item: any) => {
          return action.payload.address_id !== item?.patient_address_rel_id;
        }) ?? null;
      state.getAddressList.data = data;
    },
    deleteAddressError: (
      state: location.LocationStateType,
      { payload: error }: PayloadAction<string>
    ) => {
      state.DeleteAddres.isLoading = false;
      state.DeleteAddres.error = error;
    },
    //pincode validation address
    pincodeValidationRequest: (
      state: location.LocationStateType,
      action: PayloadAction<any>
    ) => {
      state.pincodeValidation.isLoading = true;
      state.pincodeValidation.error = null;
    },
    pincodeValidationSuccess: (
      state: location.LocationStateType,
      action: PayloadAction<any>
    ) => {
      state.pincodeValidation.isLoading = false;
      state.pincodeValidation.data = action.payload?.data;
    },
    pincodeValidationError: (
      state: location.LocationStateType,
      { payload: error }: PayloadAction<string>
    ) => {
      state.pincodeValidation.isLoading = false;
      state.pincodeValidation.error = error;
    },
  },
});

// Export actions and reducer
export const {
  // get states
  getStatesListRequest,
  getStatesListSuccess,
  getStatesListError,

  // get cities
  getCitiesListRequest,
  getCitiesListSuccess,
  getCitiesListError,

  //get address list
  getAddressListRequest,
  getAddressListSuccess,
  getAddressListError,

  //add and update address
  updateAddressRequest,
  updateAddressSuccess,
  updateAddressError,

  //delete address
  deleteAddressRequest,
  deleteAddressSuccess,
  deleteAddressError,

  //pincode validation
  pincodeValidationRequest,
  pincodeValidationSuccess,
  pincodeValidationError,
} = locationSlice.actions;

export default locationSlice.reducer;
